import { Plugin } from '@tiptap/pm/state';
import type { EditorView } from '@tiptap/pm/view';

const DRAG_IMAGE_OFFSET = {
  X: 10,
  Y: -8,
};

// drop indicator 是直接挂到 document.body 上的游离元素，没有主题上下文。
// 用 mix-blend-mode: difference + 纯白底色，使 indicator 在 light（白底）和
// dark（深色底）下都呈现高对比的颜色，不再出现 light 模式下白对白看不见的问题。
// 再叠一层柔和阴影增强可见性。
const DROP_INDICATOR_STYLE = {
  position: 'absolute',
  width: '2px',
  height: '0',
  backgroundColor: '#ffffff',
  mixBlendMode: 'difference',
  boxShadow: '0 0 0 1px rgba(0,0,0,0.18)',
  pointerEvents: 'none',
  zIndex: 100,
  transition: 'transform 0.1s ease',
  display: 'none',
  willChange: 'transform, top, left',
} as const;

let dropIndicator: HTMLElement | null = null;

export interface DragDropPluginOptions {
  nodeClassName: string;
}

export function createTipTapDragDropPlugin(options: DragDropPluginOptions): Plugin {
  const { nodeClassName } = options;

  return new Plugin({
    props: {
      handleDOMEvents: {
        dragstart: (view, event) => handleDragStart(view, event, nodeClassName),
        dragover: handleDragOver,
        dragleave: handleDragLeave,
        dragend: handleDragEnd,
        drop: handleDragEnd,
      },
    },
  });
}

function handleDragStart(_: EditorView, event: DragEvent, nodeClassName: string): boolean {
  if (!event.target) return false;

  const target = event.target;
  if (!(target instanceof HTMLElement)) return false;

  const draggableNode = target.classList.contains(nodeClassName) ? target : target.closest(`.${nodeClassName}`);
  if (!draggableNode) return false;

  if (event.dataTransfer) {
    event.dataTransfer.setDragImage(draggableNode, DRAG_IMAGE_OFFSET.X, DRAG_IMAGE_OFFSET.Y);
  }

  createDropIndicator();
  return false;
}

function createDropIndicator() {
  if (dropIndicator) return;

  dropIndicator = document.createElement('div');
  Object.assign(dropIndicator.style, DROP_INDICATOR_STYLE);
  document.body.appendChild(dropIndicator);
}

function removeDropIndicator() {
  if (dropIndicator?.parentNode) {
    dropIndicator.parentNode.removeChild(dropIndicator);
    dropIndicator = null;
  }
}

function handleDragOver(view: EditorView, event: DragEvent): boolean {
  if (!dropIndicator) return false;

  const coords = view.posAtCoords({ left: event.clientX, top: event.clientY });
  if (!coords) return false;

  const domCoords = view.coordsAtPos(coords.pos);

  if (domCoords) {
    dropIndicator.style.left = `${domCoords.left}px`;
    dropIndicator.style.top = `${domCoords.top}px`;
    dropIndicator.style.height = `${domCoords.bottom - domCoords.top}px`;
    dropIndicator.style.display = 'block';
  }

  return false;
}

function handleDragLeave(): boolean {
  if (dropIndicator) {
    dropIndicator.style.display = 'none';
  }

  return false;
}

function handleDragEnd(): boolean {
  removeDropIndicator();
  return false;
}
